import {
  PROCESS_GROUPS,
  certificatesAllowedFor,
  certificatesFor,
  finishesFor,
  inspectionsFor,
  materialsFor,
} from '@misumi/shared'
import type { PartProcess, ProcessMaterialOption } from '@misumi/shared'
import { useState } from 'react'

/**
 * UID-346 (ADR-0117 D9) — the process → material → finish cascade + material-
 * gated inspections/certificates state EXTRACTED from
 * `PartsBulkConfigureModal.tsx` (UID-46/UID-257) so the RFQ vertical's Bulk
 * configure modal (`ConfigurePartsModal.tsx`) can share it rather than
 * reimplementing the same gating rules a second time. This is the ONE piece
 * genuinely worth sharing between the two modals: the cascade/reset/gating
 * RULES (pick a process → material list resets, pick a material → finish/
 * inspections/certificates all reset and become available) are exactly the
 * kind of logic that silently drifts if hand-copied, and both modals must
 * agree on it byte-for-byte (ADR-0117 D1 — "no hand-typed process, material,
 * finish, inspection or certificate list may be introduced in the RFQ
 * vertical").
 *
 * DELIBERATELY NOT extracted alongside this: the modal CHROME (title, footer
 * copy, Certificates control — a plain multi-select checkbox fieldset in
 * the parts vertical, `PartsBulkConfigureModal.tsx`, vs. a single-select
 * `<select>` in the RFQ vertical's `ConfigurePartsModal.tsx`, UID-600) and
 * the price-estimate source (the parts vertical's existing client-
 * side average of already-loaded rows vs. the RFQ vertical's real
 * `POST /pricing/configuration-estimate` call, AC4). Those differ by design
 * between the two verticals — the parts vertical's own pinned test file
 * (`PartsBulkConfigureModal.test.tsx`) is the safety net proving this
 * extraction changed no observable behavior there — and forcing them into one
 * shared shape would mean either changing the parts vertical's pinned copy
 * (explicitly out of scope: "the parts vertical must keep working") or
 * diluting the RFQ modal's fidelity to the design AC1/AC7 require literally.
 * Sharing the STATE MACHINE while letting each vertical own its own
 * rendering is the bounded, safe version of "do not duplicate 451 lines":
 * it is precisely the part that was actually at risk of drifting.
 *
 * The catalog itself is READ DIRECTLY from `@misumi/shared` (ADR-0117 D1/D2)
 * rather than threaded through as a parameter — there is exactly one real
 * catalog in this app (the ERP-derived data behind `materialsFor`/
 * `finishesFor`/`certificatesFor`/`inspectionsFor`/`certificatesAllowedFor`
 * and `PROCESS_GROUPS`, UID-93), and D1's binding rule is that no second one
 * may ever exist to inject. Reading the shared ACCESSORS rather than indexing
 * `MATERIALS_BY_PROCESS` directly (code review W2, 2026-08-27) means this
 * file and the server's `validateSpecification` share one implementation of
 * "what does this process/material offer", not two that can drift.
 */

/** UID-93: the real, ordered process taxonomy (instant/manual-quote first,
 * then the 48-hour-manual-quote group) — never a second, hand-typed process
 * list (ADR-0117 D1). */
export const CONFIG_PROCESS_OPTIONS: ReadonlyArray<{
  readonly id: PartProcess
  readonly label: string
}> = [...PROCESS_GROUPS.instantManual, ...PROCESS_GROUPS.manualQuote]

export function toggleListItem(list: readonly string[], item: string): readonly string[] {
  return list.includes(item) ? list.filter((i) => i !== item) : [...list, item]
}

/**
 * The design's synthesized "no secondary operation" choice (`dc.html:8265`'s
 * `{ id: '__std', name: 'As-machined / standard' }`).
 *
 * This is NOT a catalog finish and must never be sent to the server — it is the
 * UI's way of letting a customer say "leave it as machined", which the wire
 * format spells `finish: null` (the server-side validator's own documented
 * "only valid" answer for the 28 of 190 materials with `finishes: []`, and an
 * always-legal answer for the other 162 since `finish` is non-mandatory).
 * `toWireFinish` below is the one seam that performs that translation.
 *
 * Introducing a synthetic name into a control otherwise fed by the real catalog
 * (ADR-0117 D1 forbids hand-typed catalog lists) is only safe while the name is
 * unambiguous, so it was checked against the catalog before being written: all
 * 190 materials in `packages/shared/src/manufacturing-catalog.ts` yield 102
 * distinct finish names, and NONE collides with this string — not exactly, not
 * case-insensitively, and not ignoring whitespace/punctuation. No catalog
 * finish id is `'__std'` either. Re-run that check if the catalog is
 * regenerated from a new ERP export: a collision would make a real finish
 * indistinguishable from "no finish" and silently drop it on the wire, so
 * `SENTINEL_FINISH_ID`/`AS_MACHINED_FINISH` must be renamed rather than shipped
 * ambiguous.
 */
export const AS_MACHINED_FINISH = 'As-machined / standard'

/** The design's id for the {@link AS_MACHINED_FINISH} option. Kept separate
 * from the display name so the Finish control can key options by `id` exactly
 * as it does for real catalog `FinishOption`s. */
const SENTINEL_FINISH_ID = '__std'

/**
 * Cascade `finish` state → the wire value for `RfqLineSpecification.finish`.
 *
 * - the {@link AS_MACHINED_FINISH} sentinel → `null` ("no secondary operation")
 * - `''` / whitespace (no decision yet) → `null` (Finish stays non-mandatory,
 *   see `finishRequired`)
 * - a real catalog finish → itself
 *
 * Canonicalization is TRIM-ONLY, deliberately matching the server's
 * `parseSpecification` (`packages/shared/src/manufacturing-catalog-lookup.ts`),
 * which persists `finish.trim()` and compares finish names case-SENSITIVELY.
 * Case-folding here would produce values the server rejects (the catalog
 * carries both `"Media Blasting"` and the id `"media-blasting"`, so casing is
 * load-bearing), so it is not done.
 */
export function toWireFinish(finish: string): string | null {
  const trimmed = finish.trim()
  if (trimmed.length === 0 || trimmed === AS_MACHINED_FINISH) {
    return null
  }
  return trimmed
}

export interface ManufacturingConfigCascade {
  readonly process: PartProcess
  readonly material: string
  readonly finish: string
  readonly inspections: readonly string[]
  readonly certificates: readonly string[]
  readonly quantityText: string

  setProcess(next: PartProcess): void
  setMaterial(next: string): void
  setFinish(next: string): void
  toggleInspection(id: string): void
  toggleCertificate(id: string): void
  setQuantityText(next: string): void

  /** Every material the current `process` offers (UID-93 order). */
  readonly materialsForProcess: readonly ProcessMaterialOption[]
  /** The catalog entry for `material`, or `undefined` before one is chosen. */
  readonly selectedMaterialOption: ProcessMaterialOption | undefined
  readonly materialChosen: boolean
  readonly availableFinishes: ProcessMaterialOption['finishes']
  /**
   * Every finish the Finish control should render: the always-present
   * as-machined sentinel followed by the material's real catalog finishes.
   * Empty until a material is chosen.
   *
   * This is a RENDERING concern only — a strict superset of
   * `availableFinishes`, kept as a separate member precisely so that
   * `finishRequired`/`ready` can keep deriving from the RAW
   * `availableFinishes`. Adding the sentinel to `availableFinishes` itself
   * would make every material look like it "offers a finish", re-breaking the
   * C2 fix below for the 28 finish-less materials, and would change what the
   * Parts Library modal (`PartsBulkConfigureModal.tsx`) renders — that modal
   * consumes this same hook, reads only `availableFinishes`, and is
   * deliberately left behaviorally identical by this change.
   */
  readonly finishOptions: readonly { readonly id: string; readonly name: string }[]
  readonly finishChosen: boolean
  /**
   * Code review C2 (2026-08-27) — whether the chosen material's catalog entry
   * offers ANY secondary finish. 28 of 190 catalog materials (e.g. "101
   * Copper", "ABS", "Delrin 150") have `finishes: []`; for those, `finish:
   * null` ("as machined / standard") is the server-side validator's OWN
   * documented "only valid" answer (`manufacturing-catalog-lookup.ts`'s
   * `finishesFor` doc comment) — so a finish SELECTION cannot ever be
   * required for them without making the modal permanently un-saveable.
   * `ready` below reads this instead of unconditionally requiring
   * `finishChosen`.
   */
  readonly finishRequired: boolean
  /** False only for `urethane_casting` (UID-93) — certificates are never
   * offered for that process, independent of which material is chosen. */
  readonly certificatesAllowed: boolean
  readonly availableCertificates: readonly string[]
  readonly availableInspections: readonly string[]
  /**
   * Material is set, AND (a finish is set OR none is required). The one gate
   * every caller of this cascade needs for its own Save/Apply-button-
   * enablement rule. Fixed by C2 (2026-08-27): previously
   * `materialChosen && finishChosen` unconditionally, which made Save
   * permanently unreachable for every finish-less material — there was no
   * finish to choose, so `finishChosen` could never become true.
   */
  readonly ready: boolean
}

/**
 * The shared cascade state (see module doc comment). `initialProcess`
 * defaults to the catalog's first `CONFIG_PROCESS_OPTIONS` entry, matching
 * both existing callers' prior behavior.
 */
export function useManufacturingConfigCascade(
  initialProcess: PartProcess = CONFIG_PROCESS_OPTIONS[0]?.id ?? 'cnc_machining',
): ManufacturingConfigCascade {
  const [process, setProcessState] = useState<PartProcess>(initialProcess)
  const [material, setMaterialState] = useState('')
  const [finish, setFinish] = useState('')
  const [inspections, setInspections] = useState<readonly string[]>([])
  const [certificates, setCertificates] = useState<readonly string[]>([])
  const [quantityText, setQuantityText] = useState('')

  // W2 (code review, 2026-08-27) — read the shared catalog ACCESSORS
  // (`materialsFor`/`finishesFor`/`certificatesFor`/`inspectionsFor`/
  // `certificatesAllowedFor`, `packages/shared/src/manufacturing-catalog-
  // lookup.ts`) rather than re-deriving the same lookups inline against
  // `MATERIALS_BY_PROCESS` — the same functions the server's
  // `validateSpecification` calls, so there is one owner per rule instead of
  // two copies that can drift (as `certificatesAllowed`'s default already
  // had: this file defaulted permissive-on-unknown via `?? true`, the server
  // is restrictive-on-unknown — `certificatesAllowedFor` below is the
  // server's own restrictive answer).
  const materialsForProcess = materialsFor(process)
  const selectedMaterialOption = materialsForProcess.find((m) => m.name === material)
  const materialChosen = material.trim().length > 0
  const availableFinishes = finishesFor(process, material)
  const finishChosen = finish.trim().length > 0
  // Rendering-only superset of `availableFinishes` (see the interface doc).
  // `finishRequired` below intentionally still reads the RAW `availableFinishes`.
  const finishOptions: readonly { readonly id: string; readonly name: string }[] = materialChosen
    ? [{ id: SENTINEL_FINISH_ID, name: AS_MACHINED_FINISH }, ...availableFinishes]
    : []
  const finishRequired = materialChosen && availableFinishes.length > 0
  const certificatesAllowed = certificatesAllowedFor(process)
  const availableCertificates = certificatesFor(process, material)
  const availableInspections = inspectionsFor(process, material)
  const ready = materialChosen && (!finishRequired || finishChosen)

  function setProcess(next: PartProcess) {
    setProcessState(next)
    setMaterialState('')
    setFinish('')
    setInspections([])
    setCertificates([])
  }

  function setMaterial(next: string) {
    setMaterialState(next)
    setFinish('')
    setInspections([])
    setCertificates([])
  }

  return {
    process,
    material,
    finish,
    inspections,
    certificates,
    quantityText,
    setProcess,
    setMaterial,
    setFinish,
    toggleInspection: (id) => setInspections((prev) => toggleListItem(prev, id)),
    toggleCertificate: (id) => setCertificates((prev) => toggleListItem(prev, id)),
    setQuantityText,
    materialsForProcess,
    selectedMaterialOption,
    materialChosen,
    availableFinishes,
    finishOptions,
    finishChosen,
    finishRequired,
    certificatesAllowed,
    availableCertificates,
    availableInspections,
    ready,
  }
}
